/**
 * @file RecoveryBanner.tsx
 * @description Recovery banner for resuming abandoned payment sessions (Task 27.1)
 * 
 * PRD Reference: §6.2 Lifecycle & Recovery, §9.2 Accessibility
 */

import React from 'react';
import styled from 'styled-components';

import { PaymentSessionSchema } from './paymentSessionStorage';
import { getRecoveryTranslations, interpolate, SupportedLocale } from './i18n';

export interface RecoveryBannerProps {
  /** Session data to display */
  session: PaymentSessionSchema | null;
  /** Callback when user clicks Resume */
  onResume: (session: PaymentSessionSchema) => void;
  /** Callback when user clicks Dismiss */
  onDismiss: () => void;
  /** Loading state */
  isLoading?: boolean;
  /** Locale for i18n (default: 'en') */
  locale?: SupportedLocale;
}

const BannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const BannerTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
`;

const BannerDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #78350f;
`;

const AmountDisplay = styled.span`
  font-weight: 600;
`;

const TimeRemaining = styled.span`
  font-size: 12px;
  color: #a16207;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const ResumeButton = styled.button`
  padding: 8px 16px;
  background-color: #f59e0b;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #d97706;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #f59e0b;
    outline-offset: 2px;
  }
`;

const DismissButton = styled.button`
  padding: 8px 16px;
  background-color: transparent;
  color: #92400e;
  border: 1px solid #92400e;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #fef3c7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #92400e;
    outline-offset: 2px;
  }
`;

const LoadingIndicator = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f59e0b;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * Calculate time remaining until expiry
 */
const getTimeRemaining = (expiresAt: number): string => {
  const now = Date.now();
  const remaining = expiresAt - now;

  if (remaining <= 0) return '0 minutes';

  const minutes = Math.ceil(remaining / (60 * 1000));
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
};

/**
 * RecoveryBanner component for resuming abandoned payment sessions
 * 
 * Accessibility:
 * - role="status" for screen reader announcement
 * - aria-live="polite" for non-intrusive updates
 * - aria-label for descriptive context
 */
export const RecoveryBanner: React.FC<RecoveryBannerProps> = ({
  session,
  onResume,
  onDismiss,
  isLoading = false,
  locale = 'en',
}) => {
  if (!session) return null;

  const t = getRecoveryTranslations(locale);
  const timeRemaining = getTimeRemaining(session.expiresAt);
  const hasAmount = session.amount && session.currency;

  const handleResume = () => {
    onResume(session);
  };

  return (
    <BannerContainer
      role="status"
      aria-live="polite"
      aria-label={t.ariaLabel}
      data-testid="recovery-banner"
    >
      <BannerTitle>{t.title}</BannerTitle>
      <BannerDescription>
        {t.description}
        {hasAmount && (
          <>
            {' '}
            <AmountDisplay>
              {interpolate(t.amountLabel, { amount: session.amount, currency: session.currency })}
            </AmountDisplay>
          </>
        )}
      </BannerDescription>
      <TimeRemaining>
        {interpolate(t.expiresIn, { time: timeRemaining })}
      </TimeRemaining>

      <ButtonContainer>
        <ResumeButton
          onClick={handleResume}
          disabled={isLoading}
          aria-label={t.resumeAriaLabel}
        >
          {isLoading ? <LoadingIndicator data-testid="loading-indicator" /> : t.resumeButton}
        </ResumeButton>
        <DismissButton
          onClick={onDismiss}
          disabled={isLoading}
          aria-label={t.dismissAriaLabel}
        >
          {t.dismissButton}
        </DismissButton>
      </ButtonContainer>
    </BannerContainer>
  );
};

export default RecoveryBanner;
